import { useEffect, useMemo, type CSSProperties } from 'react';
import { GOOGLE_FONTS, themeCssVars, type ThemeTokens } from '@ugc/shared';

/**
 * React's CSSProperties has no index signature, so a custom-property bag is not
 * assignable to it. This is the one place that cast lives: every other call site
 * builds its vars as a plain record and hands them here.
 */
export function cssVars(vars: Record<string, string>): CSSProperties {
  return vars as CSSProperties;
}

/**
 * The inline style that themes a whole profile subtree. `themeCssVars` from
 * @ugc/shared is the only definition of what a token becomes in CSS — the
 * editor's live preview and the published page must never disagree about that,
 * so nothing here re-derives it.
 */
export function useThemeVars(tokens: ThemeTokens): CSSProperties {
  return useMemo(() => cssVars(themeCssVars(tokens)), [tokens]);
}

/**
 * Weights Google Fonts actually serves per family, and which of those weights
 * also ship a true italic.
 *
 * This table is not decoration: the css2 endpoint answers 400 for the *entire*
 * request when any family is asked for a weight or an `ital` axis it does not
 * have, which would drop both faces at once. Theme tokens are creator-editable
 * (a custom theme can pair a 300 weight with a family whose lightest cut is
 * 400), so every requested axis is snapped to something the family really has.
 * A family missing from this table falls back to a bare `family=` request,
 * which always resolves.
 */
interface FontAvailability {
  weights: readonly number[];
  italics: readonly number[];
}

const FONT_AVAILABILITY: Readonly<Record<string, FontAvailability>> = {
  'Cormorant Garamond': { weights: [300, 400, 500, 600, 700], italics: [300, 400, 500, 600, 700] },
  'Playfair Display': { weights: [400, 500, 600, 700, 800, 900], italics: [400, 500, 600, 700, 800, 900] },
  Fraunces: { weights: [300, 400, 500, 600, 700], italics: [300, 400, 500, 600, 700] },
  Marcellus: { weights: [400], italics: [] },
  'Bodoni Moda': { weights: [400, 500, 600, 700, 800, 900], italics: [400, 500, 600, 700, 800, 900] },
  Italiana: { weights: [400], italics: [] },
  'Libre Baskerville': { weights: [400, 700], italics: [400] },
  Syne: { weights: [400, 500, 600, 700, 800], italics: [] },
  Jost: { weights: [300, 400, 500, 600, 700], italics: [300, 400, 500, 600, 700] },
  Inter: { weights: [300, 400, 500, 600, 700], italics: [] },
  Karla: { weights: [300, 400, 500, 600, 700], italics: [] },
  'Work Sans': { weights: [300, 400, 500, 600, 700], italics: [] },
  'DM Sans': { weights: [300, 400, 500, 600, 700], italics: [] },
};

/** Weights body copy uses: regular plus the medium used for small caps labels. */
const BODY_WEIGHTS = [400, 500];

/**
 * The Google Fonts URL for exactly the two families a theme uses, or null when
 * neither survives validation.
 *
 * Family names are matched against the @ugc/shared allowlist rather than
 * escaped, because an unrecognised name means a corrupt or hostile token — the
 * right answer is to drop it and let the CSS fallback stack render, not to
 * request an arbitrary string from a third-party origin.
 */
export function googleFontsHref(tokens: ThemeTokens): string | null {
  const displayWeight = clampWeight(tokens.displayWeight);

  // One family cannot appear twice in a css2 URL, so a theme that uses the same
  // face for both roles is requested once with the union of what it needs.
  const specs =
    tokens.displayFont === tokens.bodyFont
      ? [familySpec(tokens.displayFont, [displayWeight, ...BODY_WEIGHTS], true)]
      : [
          familySpec(tokens.displayFont, [displayWeight], true),
          familySpec(tokens.bodyFont, BODY_WEIGHTS, false),
        ];

  const usable = specs.filter((spec): spec is string => spec !== null);
  if (usable.length === 0) return null;
  return `https://fonts.googleapis.com/css2?${usable.join('&')}&display=swap`;
}

/**
 * Injects the theme's font stylesheet once per URL.
 *
 * Nothing is removed on unmount: the editor swaps themes on every keystroke,
 * and tearing a stylesheet down would flash the fallback face each time the
 * creator returns to a preset they already tried. A handful of idle <link>
 * elements costs nothing; a flashing page costs the sale.
 */
export function useGoogleFonts(tokens: ThemeTokens): void {
  // Keyed on the three tokens that can change the URL, not on the token object:
  // in the editor that object is rebuilt on every keystroke, and a colour edit
  // must not re-run an effect that touches the network.
  const href = useMemo(
    () => googleFontsHref(tokens),
    [tokens.displayFont, tokens.bodyFont, tokens.displayWeight],
  );

  useEffect(() => {
    if (href === null || injectedHrefs.has(href)) return;
    injectedHrefs.add(href);
    ensurePreconnect();

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-ugc-fonts', '');
    document.head.append(link);
  }, [href]);
}

const injectedHrefs = new Set<string>();

/**
 * Google serves font files from a second origin, so the handshake for the woff2
 * cannot start until the CSS has parsed. Warming both origins up front is worth
 * roughly a paint on the largest text on the page.
 */
function ensurePreconnect(): void {
  if (document.head.querySelector('link[data-ugc-fonts-preconnect]') !== null) return;

  for (const origin of ['https://fonts.googleapis.com', 'https://fonts.gstatic.com']) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    // The font files are served cross-origin without credentials; omitting this
    // opens a second connection and wastes the preconnect entirely.
    if (origin.includes('gstatic')) link.crossOrigin = '';
    link.setAttribute('data-ugc-fonts-preconnect', '');
    document.head.append(link);
  }
}

/** One `family=` query parameter, or null when the family is not allow-listed. */
function familySpec(family: string, wanted: number[], wantItalic: boolean): string | null {
  const name = family.trim();
  if (!GOOGLE_FONTS.includes(name)) return null;

  // Spaces are '+' in the css2 API; everything else is percent-encoded, though
  // the allowlist means only ASCII letters and spaces can reach this line.
  const encoded = encodeURIComponent(name).replace(/%20/g, '+');

  const available = FONT_AVAILABILITY[name];
  if (available === undefined) return `family=${encoded}`;

  const resolved = uniqueSorted(
    wanted
      .map((weight) => nearestWeight(available.weights, weight))
      .filter((weight): weight is number => weight !== undefined),
  );
  if (resolved.length === 0) return `family=${encoded}`;

  const italics = wantItalic
    ? resolved.filter((weight) => available.italics.includes(weight))
    : [];
  if (italics.length === 0) return `family=${encoded}:wght@${resolved.join(';')}`;

  // css2 requires axis tuples in ascending order: all upright, then all italic.
  const tuples = [
    ...resolved.map((weight) => `0,${weight}`),
    ...italics.map((weight) => `1,${weight}`),
  ];
  return `family=${encoded}:ital,wght@${tuples.join(';')}`;
}

function nearestWeight(available: readonly number[], wanted: number): number | undefined {
  let best: number | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const weight of available) {
    const distance = Math.abs(weight - wanted);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = weight;
    }
  }
  return best;
}

function uniqueSorted(values: number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}

function clampWeight(weight: number): number {
  if (!Number.isFinite(weight)) return 400;
  return Math.min(900, Math.max(100, Math.round(weight)));
}
